/**
 * The Benefits tab on a member's profile.
 *
 * It answers one question in one place: if we pay her, what happens to what she
 * already receives. That takes three things in a fixed order —
 *
 *   1. which state's rules apply (without it nothing below can run),
 *   2. what she told us she receives,
 *   3. what the rules say, and how much room is left in this period.
 *
 * Each step says plainly when it cannot answer. The one failure mode this
 * screen must not have is looking complete while something is missing, because
 * a blank where a warning belongs reads as "fine".
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BenefitImpactPanel } from './BenefitImpactPanel';
import { BenefitsIntakeQuestionnaire } from './BenefitsIntakeQuestionnaire';
import { MemberStateCodeField } from './MemberStateCodeField';
import { PeriodHeadroomMeter } from './PeriodHeadroomMeter';
import { PublicChargeExplainer } from './PublicChargeExplainer';
import { benefitsApi, type MemberImpactResult } from './types';

interface Props {
  memberId: number;
  /** The programme whose envelope governs her payments, when there is one. */
  programId?: number | null;
  readOnly?: boolean;
}

export const MemberBenefitsTab: React.FC<Props> = ({ memberId, programId, readOnly }) => {
  const [impact, setImpact] = useState<MemberImpactResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intakeOpen, setIntakeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setImpact(await benefitsApi.memberImpact(memberId, programId ?? undefined));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the benefit impact.');
    } finally {
      setLoading(false);
    }
  }, [memberId, programId]);

  useEffect(() => {
    void load();
  }, [load]);

  const needsIntake = !!impact && impact.intake === null;
  const needsState = !!impact && !impact.state_code;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Benefits</h2>
          <p className="text-sm text-muted-foreground">
            What a payment would do to the benefits this household already receives.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="mr-1 h-4 w-4" aria-hidden="true" />
          )}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <MemberStateCodeField memberId={memberId} readOnly={readOnly} onChanged={() => void load()} />

      {impact?.envelope && (
        <PeriodHeadroomMeter envelope={impact.envelope} />
      )}

      {/* 2. The intake */}
      {intakeOpen ? (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Benefits intake</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setIntakeOpen(false)}>
              Close
            </Button>
          </CardHeader>
          <CardContent>
            <BenefitsIntakeQuestionnaire
              memberId={memberId}
              onSubmitted={() => {
                setIntakeOpen(false);
                void load();
              }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="flex items-start gap-2">
              <ClipboardList className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {needsIntake ? 'No benefits intake yet' : 'Benefits intake on file'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {needsIntake
                    ? 'Six questions. Without them the rules have nothing to read, and nothing below can be answered.'
                    : impact?.intake?.benefits_received.length
                      ? `Receiving: ${impact.intake.benefits_received.join(', ')}`
                      : 'No benefits were recorded.'}
                </p>
              </div>
            </div>
            {!readOnly && (
              <Button size="sm" variant={needsIntake ? 'default' : 'outline'} onClick={() => setIntakeOpen(true)}>
                {needsIntake ? 'Start intake' : 'Update intake'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* 3. What the rules say */}
      {loading && !impact ? (
        <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Reading the rules…
        </div>
      ) : (
        <BenefitImpactPanel
          assessment={impact?.assessment ?? null}
          blockedReason={needsState || needsIntake ? impact?.blocked_reason : null}
        />
      )}

      <PublicChargeExplainer />
    </div>
  );
};

export default MemberBenefitsTab;
